/*
 * Authors: mariushirjanu, Koen, Triadi
 * Created: 03/09/2019
 * Upgraded: 27/07/2023
 */
import { HttpDataService } from '../../shared/http/http-data.service';
import { GA1Service, MESSAGE_PAYMENT_SAVE_FAILED, MESSAGE_PAYMENT_SAVED, MESSAGE_PAYMENT_SAVING, MESSAGE_PAYMENT_UPDATED } from '../../shared/ga1/ga1.service';
import { util } from '../../shared/util';
import { Inject, Injectable } from '@angular/core';
import { IExtendedWindow } from '../../../type_definitions/datatypes';
import { GaMessageService } from '../../shared/message/ga-message.service';

declare const Stripe:any;

export interface IOrderPaymentDetails {
  localPaymentTerms:string;
  centralPaymentTerms:string;
  possiblePaymentTypes:string[];
  paymentType:string;
  poNumber:string;
  paymentDate?:number;
  paymentTotal?:number;
  paymentCurrency?:string;
  paymentStatus?:string;
  poType:string;
  minimalOrderStateReached:boolean;
  testOrder:boolean;
  orderState:string;
  billingType:string;
  prepaid:boolean;
}

export interface IOrderPaymentUpdate {
  paymentType:string;
  poNumber:string;
  poType?:string;
}

interface IStripeAccount {
  publishKey:string;
  stripeAccountType:string;
  stripeConnectKey:string;
}


export const PAYMENT_STATUS_OPEN:string = 'OPEN';
export const PAYMENT_STATUS_REFUSED:string = 'REFUSED';
export const PAYMENT_TYPE_CREDITCARD:string = 'CC';
export const PAYMENT_TYPE_PAYPAL:string = 'PP';
export const PAYMENT_TYPE_BANK:string = 'PO';

const STRIPE_CONNECT:string = 'STRIPE_CONNECT';

export enum POType {
  MANUAL = 'MANUAL',
  COPY = 'COPY',
  BLANKET = 'BLANKET',
  MANDATORY = 'MANDATORY'
}

@Injectable()
export class PaymentDataService {
  constructor(
    private httpDataService:HttpDataService,
    private messageService:GaMessageService,
    private ga1Service:GA1Service,
    @Inject(Window) private window:IExtendedWindow
  ) {
  }

  public loadOrderPaymentDetails(orderId:number):Promise<IOrderPaymentDetails> {
    return this.httpDataService.doGET(
      `rest/orderentry/orders/${orderId}/paymentinfo`,
      undefined,
      undefined,
      'Error loading order payment details'
    );
  }

  public updateOrderPaymentDetails(orderId:number, orderPaymentUpdate:IOrderPaymentUpdate):Promise<void> {
    return this.httpDataService.doPOST(
      `rest/orderentry/orders/${orderId}/updatepayment`,
      undefined,
      orderPaymentUpdate,
      undefined,
      'Error updating order payment details'
    );
  }

  private getStripeAccountInfo(orderId:number, invoiceId:number):Promise<IStripeAccount> {
    return this.httpDataService.doGET(
      'rest/stripe/checkout/account',
      {
        orderId,
        invoiceId
      },
      undefined,
      'Error getting info'
    );
  }

  private getStripeSession(orderId:number, invoiceId:number, returnURL?:string):Promise<string> {
    if (returnURL) {
      returnURL = encodeURIComponent(returnURL);
    }

    return this.httpDataService.doPOST(
      'rest/stripe/checkout/session',
      {
        orderId,
        invoiceId,
        returnURL
      },
      undefined,
      undefined,
      'Error while processing payment'
    );
  }

  private getPayPalSession(orderId:number, invoiceId:number, returnURL?:string):Promise<string> {
    return this.httpDataService.doGET(
      'rest/paypal/order/create',
      {
        orderId,
        invoiceId,
        returnURL
      },
      undefined,
      'Error while processing payment'
    );
  }

  public payWithStripe(orderId:number, invoiceId:number, returnURL?:string):Promise<void> {
    return Promise.all([
      this.getStripeSession(orderId, invoiceId, returnURL),
      this.getStripeAccountInfo(orderId, invoiceId)
    ]).then(util.spread((sessionId:string, stripeAccountInfo:IStripeAccount) => {
      const stripeOptions:any = {};
      if (stripeAccountInfo.stripeAccountType === STRIPE_CONNECT) {
        stripeOptions.stripeAccount = stripeAccountInfo.stripeConnectKey;
      }

      const stripe:any = Stripe(stripeAccountInfo.publishKey, stripeOptions);
      return stripe.redirectToCheckout({ sessionId }).then((result) => {
        if (result && result.error) {
          this.messageService.showStatusMessage(result.error.message);
          Promise.reject(result.error.message);
        }
      });
    }));
  }

  public payWithPayPal(orderId:number, invoiceId:number, returnURL?:string):Promise<void> {
    return this.getPayPalSession(orderId, invoiceId, returnURL)
      .then((payPalRedirectUrl:string) => {
        this.window.top.location.href = payPalRedirectUrl;
      });
  }

  public notifyPaymentUpdate(paymentType:string, poNumber:string):void {
    this.ga1Service.sendMessage(MESSAGE_PAYMENT_UPDATED, {
      paymentType,
      poNumber
    });
  }

  public notifyPaymentSaved = ():void => {
    //notify ga1 that the payment has been saved
    this.ga1Service.sendMessage(MESSAGE_PAYMENT_SAVED, {});
  };

  public notifyPaymentSaveFailed = ():void => {
    //notify ga1 that the payment saving has been failed and has to be aborted
    this.ga1Service.sendMessage(MESSAGE_PAYMENT_SAVE_FAILED);
  };


  public notifyPaymentSaving = ():void => {
    // notify ga1 that the order is saving to prevent double save order
    // since ga1 has implementation 5 seconds timeout and order being saved anyway if not getting any message from ga2
    this.ga1Service.sendMessage(MESSAGE_PAYMENT_SAVING);
  };
}